<template>
	<section v-loading="loading" class="provider-store-page">
		<div v-if="shopInfo.id" class="store-content">
			<div class="store-hero">
				<div class="store-logo">
					<el-image v-if="logoUrl" :src="logoUrl" fit="cover"></el-image>
					<i v-else class="el-icon-s-shop"></i>
				</div>
				<div class="store-summary">
					<div class="store-title-row">
						<h2>{{ shopInfo.shopName || '-' }}</h2>
						<span :class="['store-state', storeStateClass]">{{ storeStateText }}</span>
					</div>
					<p>店铺编号：{{ shopInfo.shopCode || shopInfo.id || '-' }}</p>
					<div class="store-contact">
						<span>
							<i class="el-icon-user"></i>
							{{ shopInfo.contactPerson || shopInfo.contactName || '-' }}
						</span>
						<span>
							<i class="el-icon-phone-outline"></i>
							{{ shopInfo.contactPhone || shopInfo.telephone || '-' }}
						</span>
					</div>
				</div>
				<div class="store-actions">
					<el-button icon="el-icon-document" @click="handleOpenApplyRecords">申请记录</el-button>
					<el-button v-if="isClosedShop" type="primary" icon="el-icon-refresh-right" @click="handleRestartApply">
						重启店铺
					</el-button>
					<el-button v-if="isNormalShop" icon="el-icon-edit-outline" @click="handleChangeApply">
						店铺信息变更
					</el-button>
					<el-button v-if="isNormalShop" icon="el-icon-switch-button" @click="handleCloseApply">闭店申请</el-button>
				</div>
			</div>

			<div v-if="pendingApply" class="pending-apply-tip">
				<i class="el-icon-time"></i>
				<div>
					<strong>{{ pendingApplyTypeText }}正在审核中</strong>
					<p>平台审核完成前不能重复提交店铺申请，请耐心等待。</p>
				</div>
			</div>

			<div class="info-section">
				<div class="store-section-heading">
					<span class="heading-icon"><i class="el-icon-office-building"></i></span>
					<div>
						<strong>店铺信息</strong>
						<small>店铺经营主体及业务介绍</small>
					</div>
				</div>
				<div class="info-grid">
					<div class="info-item">
						<span>经营主体名称</span>
						<strong>{{ shopInfo.companyName || shopInfo.subjectName || '-' }}</strong>
					</div>
					<div class="info-item">
						<span>经营地址</span>
						<strong>{{ shopInfo.businessAddress || shopInfo.address || '-' }}</strong>
					</div>
					<div class="info-item is-full">
						<span>业务简介</span>
						<strong>{{ shopInfo.businessDesc || shopInfo.businessScope || '-' }}</strong>
					</div>
				</div>
			</div>

			<div class="info-section">
				<div class="store-section-heading">
					<span class="heading-icon"><i class="el-icon-bank-card"></i></span>
					<div>
						<strong>结算与资质信息</strong>
						<small>店铺结算账户及企业资质材料</small>
					</div>
				</div>
				<div class="info-grid">
					<div class="info-item">
						<span>开户行名称</span>
						<strong>{{ shopInfo.bankName || '-' }}</strong>
					</div>
					<div class="info-item">
						<span>开户行账号</span>
						<strong>{{ shopInfo.bankAccount || '-' }}</strong>
					</div>
					<div class="info-item is-full">
						<span>企业营业执照</span>
						<div v-if="businessLicenseFile" class="license-preview">
							<el-image
								v-if="businessLicenseFile.isImage"
								:src="businessLicenseFile.url"
								:preview-src-list="licensePreviewList"
								fit="cover"
							></el-image>
							<el-button v-else type="text" @click="handlePreviewFile(businessLicenseFile)">查看营业执照</el-button>
						</div>
						<strong v-else>暂无附件</strong>
					</div>
				</div>
			</div>
		</div>

		<div v-else class="store-empty">
			<i class="el-icon-s-shop"></i>
			<h3>暂未查询到店铺信息</h3>
			<p>店铺申请审核通过后，即可在这里查看和管理店铺。</p>
		</div>

		<jr-dialog
			:visible.sync="applyRecordVisible"
			title="店铺申请记录"
			width="1080px"
			:show-btn="false"
			custom-class="provider-apply-record-dialog"
			@close="handleCloseApplyRecords"
		>
			<template slot="content">
				<div class="apply-record-summary">
					<div>
						<strong>历史申请</strong>
						<span>可查看开店、信息变更、闭店及重启店铺申请的审核情况</span>
					</div>
					<span class="record-count">共 {{ applyRecordPage.total }} 条记录</span>
				</div>

				<el-table v-loading="applyRecordLoading" :data="applyRecordList" border stripe empty-text="暂无店铺申请记录">
					<el-table-column label="序号" width="70" align="center">
						<template slot-scope="scope">{{ getApplyRecordIndex(scope.$index) }}</template>
					</el-table-column>
					<el-table-column label="申请类型" min-width="130" align="center">
						<template slot-scope="scope">
							<span class="apply-type-tag">{{ getApplyTypeText(scope.row.applyType) }}</span>
						</template>
					</el-table-column>
					<el-table-column prop="shopName" label="店铺名称" min-width="170" show-overflow-tooltip>
						<template slot-scope="scope">{{ scope.row.shopName || shopInfo.shopName || '-' }}</template>
					</el-table-column>
					<el-table-column label="申请时间" min-width="165" align="center">
						<template slot-scope="scope">{{ getApplyTime(scope.row) }}</template>
					</el-table-column>
					<el-table-column label="审核状态" width="120" align="center">
						<template slot-scope="scope">
							<span :class="['record-audit-state', getAuditStateClass(scope.row.auditState)]">
								{{ getAuditStateText(scope.row.auditState) }}
							</span>
						</template>
					</el-table-column>
					<el-table-column label="审核意见" min-width="210" show-overflow-tooltip>
						<template slot-scope="scope">{{ getAuditOpinion(scope.row) }}</template>
					</el-table-column>
				</el-table>

				<jr-pagination
					v-if="applyRecordPage.total > applyRecordPage.limit"
					:page.sync="applyRecordPage.page"
					:limit.sync="applyRecordPage.limit"
					:total="applyRecordPage.total"
					:page-sizes="[10, 20, 30, 50]"
					@change="handleApplyRecordPageChange"
				></jr-pagination>
			</template>
		</jr-dialog>
	</section>
</template>

<script>
	import { mapGetters } from 'vuex'
	import JrDialog from '@/components/Dialog'
	import JrPagination from '@/components/Pagination'
	import { previewMerchantFile, queryMerchantFileList, queryMyShopApplyPage } from '@/api/mall'
	import {
		ENUM_SHOP_APPLY_TYPE,
		ENUM_SHOP_AUDIT_STATE,
		ENUM_SHOP_STATE,
		SHOP_APPLY_TYPE,
		SHOP_AUDIT_STATE,
		SHOP_STATE
	} from '@/enum'

	export default {
		name: 'ProviderStore',
		components: {
			JrDialog,
			JrPagination
		},
		data() {
			return {
				// 页面及附件数据加载状态。
				loading: false,
				// 当前店铺的 Logo、营业执照等附件列表。
				fileList: [],
				// 申请记录弹窗显隐状态及列表加载状态。
				applyRecordVisible: false,
				applyRecordLoading: false,
				// 当前页申请记录及分页参数，接口要求传递 page、limit。
				applyRecordList: [],
				applyRecordPage: {
					page: 1,
					limit: 10,
					total: 0
				}
			}
		},
		computed: {
			...mapGetters(['merchantShopInfo', 'merchantLatestApply']),
			// 对状态管理中的店铺数据设置空对象兜底，简化模板字段读取。
			shopInfo() {
				return this.merchantShopInfo || {}
			},
			// 从店铺附件中提取 Logo 预览地址。
			logoUrl() {
				const logo = this.fileList.find(item => item.fileBusinessType === 'SHOP_LOGO')
				return logo ? logo.url : ''
			},
			// 当前店铺的营业执照附件，用于图片预览或文件查看。
			businessLicenseFile() {
				return this.fileList.find(item => item.fileBusinessType === 'BUSINESS_LICENSE')
			},
			// Element Image 组件需要数组格式的图片预览源。
			licensePreviewList() {
				return this.businessLicenseFile && this.businessLicenseFile.isImage ? [this.businessLicenseFile.url] : []
			},
			// 将店铺状态枚举转换为页面展示文案。
			storeStateText() {
				return ENUM_SHOP_STATE[this.shopInfo.shopState] || '已认证'
			},
			// 根据店铺经营状态返回对应的状态标签样式。
			storeStateClass() {
				if (Number(this.shopInfo.shopState) === SHOP_STATE.CLOSED) return 'is-closed'
				if (Number(this.shopInfo.shopState) === SHOP_STATE.PENDING) return 'is-pending'
				return 'is-normal'
			},
			// 只有正常经营的店铺才展示资料变更和闭店申请入口。
			isNormalShop() {
				return Number(this.shopInfo.shopState) === SHOP_STATE.NORMAL
			},
			// 重启店铺入口只在店铺已经闭店时展示，其他经营状态不能发起该申请。
			isClosedShop() {
				return Number(this.shopInfo.shopState) === SHOP_STATE.CLOSED
			},
			// 提取尚未审核完成的最新申请，用于页面提示及防止重复提交。
			pendingApply() {
				return Number(this.merchantLatestApply.auditState) === SHOP_AUDIT_STATE.PENDING ? this.merchantLatestApply : null
			},
			// 将待审核申请类型转换为“开店申请、资料变更申请、闭店申请”等文案。
			pendingApplyTypeText() {
				if (Number(this.pendingApply && this.pendingApply.applyType) === SHOP_APPLY_TYPE.REAPPLY) return '重启店铺申请'
				return ENUM_SHOP_APPLY_TYPE[this.pendingApply && this.pendingApply.applyType] || '店铺申请'
			}
		},
		created() {
			this.initPage()
		},
		methods: {
			// 初始化店铺页面，并在存在店铺时继续加载店铺附件。
			async initPage() {
				this.loading = true
				try {
					// 路由守卫已完成店铺状态查询，此处复用结果，避免重复请求及覆盖刚提交的申请状态。
					await this.$store.dispatch('merchantShop/loadShopContext')
					if (this.shopInfo.id) await this.loadFiles(this.shopInfo.id)
				} finally {
					this.loading = false
				}
			},
			// 查询指定店铺的附件，并统一转换为页面使用的数据结构。
			async loadFiles(businessId) {
				const response = await queryMerchantFileList({ businessId }).catch(() => ({ data: [] }))
				this.fileList = this.normalizeFiles(response.data)
			},
			// 兼容接口可能返回的不同附件 ID、名称字段，并补充预览地址及文件类型标记。
			normalizeFiles(files) {
				if (!Array.isArray(files)) return []
				return files.map(item => {
					const fileId = item.id || item.fileId || item.fileRecordId
					const fileName = item.fileName || item.name || item.originalName || '附件'
					return {
						...item,
						fileId,
						fileName,
						url: fileId ? previewMerchantFile() + fileId : item.url || '',
						isImage: this.isImageFile(item, fileName)
					}
				})
			},
			// 通过 MIME 类型或常见图片扩展名判断附件能否使用图片预览。
			isImageFile(file, fileName) {
				const name = String(fileName).toLowerCase()
				const type = String(file.fileType || file.contentType || file.mimeType || '').toLowerCase()
				return type.includes('image') || ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'].some(ext => name.endsWith(ext))
			},
			// 非图片附件使用浏览器新窗口打开预览地址。
			handlePreviewFile(file) {
				if (file.url) window.open(file.url, '_blank')
			},
			// 打开申请记录弹窗时从第一页开始查询，确保用户先看到最新一批记录。
			handleOpenApplyRecords() {
				this.applyRecordVisible = true
				this.applyRecordPage.page = 1
				this.loadApplyRecords()
			},
			// 分页查询当前登录用户的店铺申请记录。
			async loadApplyRecords() {
				this.applyRecordLoading = true
				try {
					const response = await queryMyShopApplyPage({
						page: this.applyRecordPage.page,
						limit: this.applyRecordPage.limit
					})
					const data = response.data || {}
					this.applyRecordList = Array.isArray(data) ? data : data.records || data.list || []
					const total = Array.isArray(data) ? data.length : data.total !== undefined ? data.total : data.totalCount
					this.applyRecordPage.total = Number(total) || 0
				} catch (error) {
					this.applyRecordList = []
					this.applyRecordPage.total = 0
				} finally {
					this.applyRecordLoading = false
				}
			},
			// 分页组件变化后同步页码、每页条数并重新查询。
			handleApplyRecordPageChange({ page, limit }) {
				this.applyRecordPage.page = page
				this.applyRecordPage.limit = limit
				this.loadApplyRecords()
			},
			// 关闭弹窗只收起页面，保留已加载记录以避免关闭过程中的内容闪烁。
			handleCloseApplyRecords() {
				this.applyRecordVisible = false
			},
			// 根据当前分页位置计算跨页连续的列表序号。
			getApplyRecordIndex(index) {
				return (this.applyRecordPage.page - 1) * this.applyRecordPage.limit + index + 1
			},
			// 将申请类型枚举转换为可读文案，并为未知值提供兜底。
			getApplyTypeText(applyType) {
				if (Number(applyType) === SHOP_APPLY_TYPE.REAPPLY) return '重启店铺申请'
				return ENUM_SHOP_APPLY_TYPE[applyType] || '其他申请'
			},
			// 将审核状态枚举转换为可读文案。
			getAuditStateText(auditState) {
				return ENUM_SHOP_AUDIT_STATE[auditState] || '未知状态'
			},
			// 根据审核状态返回对应标签样式。
			getAuditStateClass(auditState) {
				if (Number(auditState) === SHOP_AUDIT_STATE.PASS) return 'is-pass'
				if (Number(auditState) === SHOP_AUDIT_STATE.REJECT) return 'is-reject'
				return 'is-pending'
			},
			// 兼容申请接口可能采用的不同申请时间字段。
			getApplyTime(record) {
				return record.createTime || record.applyTime || record.submitTime || '-'
			},
			// 兼容不同审核意见字段，未填写意见时统一展示占位符。
			getAuditOpinion(record) {
				return record.auditOpinion || record.auditRemark || record.remark || '-'
			},
			// 进入资料变更页面前先判断是否存在其他待审核店铺申请。
			handleChangeApply() {
				if (this.pendingApply) {
					this.$message.warning('已有待审核申请，请等待平台审核')
					return
				}
				this.$router.push('/provider/store/change')
			},
			// 重启申请与其他店铺申请互斥，存在待审核记录时禁止重复提交。
			handleRestartApply() {
				if (this.pendingApply) {
					this.$message.warning('已有待审核申请，请等待平台审核')
					return
				}
				this.$router.push('/provider/store/restart')
			},
			handleCloseApply() {
				// 与提交页保持双重校验，避免已有待审核申请时重复进入闭店流程。
				if (this.pendingApply) {
					this.$message.warning('已有待审核申请，请等待平台审核')
					return
				}
				this.$router.push('/provider/store/close')
			}
		}
	}
</script>

<style lang="scss" scoped>
	.provider-store-page {
		min-height: 100%;
		padding: 24px;
		border: 1px solid #e4e9ef;
		border-radius: 8px;
		background: #ffffff;
		box-sizing: border-box;
	}

	.store-hero {
		display: flex;
		align-items: center;
		padding: 26px;
		border-radius: 10px;
		background: linear-gradient(120deg, #edf5ff 0%, #f7fbff 64%, #eef8f6 100%);
	}

	.store-logo {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 78px;
		height: 78px;
		overflow: hidden;
		border: 4px solid rgba(255, 255, 255, 0.9);
		border-radius: 12px;
		color: #2671cb;
		font-size: 34px;
		background: #ffffff;
		box-shadow: 0 5px 15px rgba(38, 113, 203, 0.12);

		.el-image {
			width: 100%;
			height: 100%;
		}
	}

	.store-summary {
		min-width: 0;
		margin-left: 20px;
		flex: 1;

		p {
			margin: 8px 0 10px;
			color: #7e8999;
			font-size: 13px;
		}
	}

	.store-title-row {
		display: flex;
		align-items: center;

		h2 {
			margin: 0 12px 0 0;
			color: #293649;
			font-size: 22px;
		}
	}

	.store-state {
		padding: 4px 10px;
		border-radius: 12px;
		font-size: 12px;

		&.is-normal {
			color: #158554;
			background: #dcf3e8;
		}
		&.is-pending {
			color: #a96b11;
			background: #fff0d2;
		}
		&.is-closed {
			color: #7c8796;
			background: #e8ebef;
		}
	}

	.store-contact {
		display: flex;
		color: #58677a;
		font-size: 15px;

		span + span {
			margin-left: 28px;
		}
		i {
			margin-right: 6px;
			color: #7b9dc3;
		}
	}

	.store-actions {
		margin-left: 30px;
		text-align: right;

		.el-button {
			margin: 4px 0 4px 10px;
			border-color: #b9cee5;
			color: #386a9e;
			background: rgba(255, 255, 255, 0.8);
		}
	}

	.pending-apply-tip {
		display: flex;
		align-items: center;
		margin-top: 18px;
		padding: 14px 18px;
		border: 1px solid #f3d69d;
		border-radius: 7px;
		color: #9b681b;
		background: #fff9ec;

		> i {
			margin-right: 12px;
			font-size: 24px;
		}
		strong {
			display: block;
			font-size: 14px;
		}
		p {
			margin: 4px 0 0;
			color: #9a8563;
			font-size: 12px;
		}
	}

	.info-section {
		margin-top: 20px;
		padding: 22px 24px 24px;
		border: 1px solid #e5eaf0;
		border-radius: 8px;
		box-shadow: 0 5px 18px rgba(38, 71, 109, 0.04);
	}

	.store-section-heading {
		display: flex;
		align-items: center;
		margin-bottom: 20px;
		padding-bottom: 16px;
		border-bottom: 1px solid #e7ecf2;

		.heading-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 38px;
			height: 38px;
			margin-right: 11px;
			border-radius: 9px;
			color: #2671cb;
			font-size: 19px;
			background: #eaf3fd;
		}

		strong,
		small {
			display: block;
		}

		strong {
			color: #2f3b4d;
			font-size: 18px;
			line-height: 24px;
		}

		small {
			margin-top: 3px;
			color: #98a1ae;
			font-size: 12px;
			font-weight: normal;
		}
	}

	.info-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 14px 16px;
	}

	.info-item {
		min-width: 0;
		min-height: 76px;
		padding: 14px 16px;
		border: 1px solid #edf0f4;
		border-radius: 6px;
		background: #f8fafc;
		box-sizing: border-box;
		transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

		&.is-full {
			grid-column: 1 / -1;
		}
		&:hover {
			border-color: #cadcf0;
			background: #ffffff;
			box-shadow: 0 4px 13px rgba(36, 91, 150, 0.07);
		}
		> span {
			display: block;
			margin-bottom: 8px;
			color: #7e8998;
			font-size: 13px;
		}
		> strong {
			color: #344154;
			font-size: 16px;
			font-weight: 600;
			line-height: 25px;
			word-break: break-all;
		}
	}

	.license-preview .el-image {
		width: 116px;
		height: 82px;
		border: 1px solid #e1e6ec;
		border-radius: 5px;
	}

	.store-empty {
		padding: 100px 20px;
		text-align: center;
		color: #a0a8b4;

		> i {
			color: #c8d5e3;
			font-size: 64px;
		}
		h3 {
			margin: 18px 0 8px;
			color: #59677a;
			font-size: 18px;
		}
		p {
			margin: 0;
			font-size: 13px;
		}
	}
</style>

<style lang="scss">
	.provider-apply-record-dialog {
		.el-dialog__body {
			padding: 4px 24px 24px;
		}

		.apply-record-summary {
			display: flex;
			align-items: center;
			justify-content: space-between;
			margin-bottom: 16px;
			padding: 14px 16px;
			border-radius: 7px;
			background: #f5f8fc;

			strong,
			span {
				display: block;
			}

			strong {
				margin-bottom: 4px;
				color: #344154;
				font-size: 16px;
			}

			span {
				color: #8b96a5;
				font-size: 13px;
			}

			.record-count {
				padding: 6px 12px;
				border-radius: 14px;
				color: #3972af;
				background: #e7f1fc;
			}
		}

		.apply-type-tag,
		.record-audit-state {
			display: inline-block;
			padding: 4px 10px;
			border-radius: 12px;
			font-size: 12px;
			line-height: 18px;
		}

		.apply-type-tag {
			color: #3d6f9f;
			background: #eaf3fc;
		}

		.record-audit-state {
			&.is-pending {
				color: #a96b11;
				background: #fff0d2;
			}

			&.is-pass {
				color: #158554;
				background: #dcf3e8;
			}

			&.is-reject {
				color: #c3534c;
				background: #fde8e6;
			}
		}

		.pagination-container {
			padding-bottom: 0;
		}
	}
</style>
